<template>
  <div class="form-container mt-3">
    <form @submit.prevent="submitFaceScansForm" class="w-100 p-3">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="imagingModality">Imaging Modality:</label>
          <select
            id="imagingModality"
            v-model="faceScansForm.imagingModality"
            class="form-control"
            required
          >
            <option value="" disabled selected hidden>Select Modality</option>
            <option value="Canfield Stereoscopic">Canfield Stereoscopic (OBJ + Texture)</option>
            <option value="Canfield Stereoscopic (TOM)">Canfield Stereoscopic (TOM)</option>
            <option value="FitsYou">FitsYou</option>
          </select>

        </div>
        <div class="form-group col-md-6">
          <label for="scanDate">Scan Date:</label>
          <input
            type="date"
            id="scanDate"
            v-model="faceScansForm.scanDate"
            :max="todayDate"
            class="form-control"
            required
          />
        </div>
      </div>

      <!-- Conditional Inputs -->
      <div
        v-if="faceScansForm.imagingModality === 'Canfield Stereoscopic'"
        class="d-md-flex"
      >
        <div class="col-md-6 col-12 p-0 pr-2">
          <label for="faceMeshFile">Face Mesh File:</label>
          <div
            class="dropzone"
            @dragenter.prevent="toggleActive"
            @dragleave.prevent="toggleActive"
            @dragover.prevent
            @drop.prevent="dropFile($event, 'faceMeshFile')"
            :class="{ 'active-dropzone': active }"
          >
            <span>Drag your file to start uploading</span>
            <small>OR</small>
            <button
              @click="getFile('faceMeshFile')"
              class="create_btn px-4 fw-normal"
            >
              Browse File
            </button>
            <input
              type="file"
              id="faceMeshFile"
              ref="faceMeshFile"
              @change="dropFile($event, 'faceMeshFile')"
              required
            />
          </div>
          <p>{{ faceMeshFile_name }}</p>
        </div>
        <div class="col-md-6 col-12 p-0 pl-2">
          <label for="textureFile">Texture File:</label>
          <div
            class="dropzone"
            @dragenter.prevent="toggleActive"
            @dragleave.prevent="toggleActive"
            @dragover.prevent
            @drop.prevent="dropFile($event, 'textureFile')"
            :class="{ 'active-dropzone': active }"
          >
            <span>Drag your file to start uploading</span>
            <small>OR</small>
            <button
              @click="getFile('textureFile')"
              class="create_btn px-4 fw-normal"
            >
              Browse File
            </button>
            <input
              type="file"
              id="textureFile"
              ref="textureFile"
              @change="dropFile($event, 'textureFile')"
              required
            />
          </div>
          <p>{{ textureFile_name }}</p>
        </div>
      </div>

      <!-- TOM (Canfield .TOM) -->
      <div
        v-else-if="faceScansForm.imagingModality === 'Canfield Stereoscopic (TOM)'"
        class="col-md-6 col-12 p-0 pr-2"
      >
        <label for="tomFile">TOM File:</label>
        <div
          class="dropzone"
          @dragenter.prevent="toggleActive"
          @dragleave.prevent="toggleActive"
          @dragover.prevent
          @drop.prevent="dropFile($event, 'tomFile')"
          :class="{ 'active-dropzone': active }"
        >
          <span>Drag your .tom file here</span>
          <small>OR</small>
          <button @click="getFile('tomFile')" class="create_btn px-4 fw-normal">
            Browse File
          </button>
          <input
            type="file"
            id="tomFile"
            ref="tomFile"
            accept=".tom"
            @change="dropFile($event, 'tomFile')"
            required
          />
        </div>
        <p>{{ tomFile_name }}</p>
      </div>


      <div
        v-else-if="faceScansForm.imagingModality === 'FitsYou'"
        class="col-md-6 col-12 p-0 pr-2"
      >
        <label for="zipFile">ZIP File:</label>
        <div
          class="dropzone"
          @dragenter.prevent="toggleActive"
          @dragleave.prevent="toggleActive"
          @dragover.prevent
          @drop.prevent="dropFile($event, 'zipFile')"
          :class="{ 'active-dropzone': active }"
        >
          <span>Drag your file to start uploading</span>
          <small>OR</small>
          <button @click="getFile('zipFile')" class="create_btn px-4 fw-normal">
            Browse File
          </button>
          <input
            type="file"
            id="zipFile"
            ref="zipFile"
            @change="dropFile($event, 'zipFile')"
            required
          />
        </div>
        <p>{{ zipFile_name }}</p>
      </div>

      <div class="d-flex align-items-center mt-1">
        <button type="submit" class="btn custom-submit-btn">
          Upload Face Scans
        </button>
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm ml-2"
        ></span>
      </div>
      
    </form>
    <VTKModalViewer
  :file="pendingFaceMeshFile"
  :visible="showVTKModal"
  @closed="vtkClosed"
  @confirmed="vtkConfirmed"
/>

  </div>
</template>

<script>
import VTKModalViewer from "./FaceAdjustmentModal.vue";
export default {
  components: { VTKModalViewer },
  props: {
    patient_id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      faceScansForm: {
        imagingModality: "",
        scanDate: "",
        faceMeshFile: null,
        textureFile: null,
        zipFile: null,
        tomFile: null, 
      },
      showVTKModal: false,
        pendingFaceMeshFile: null,
        modifiedFaceMeshBlob: null,
      todayDate: new Date().toISOString().split("T")[0],
      active: false,
      faceMeshFile_name: "",
      textureFile_name: "",
      tomFile_name: "",
      zipFile_name: "",
      loading: false,
    };
  },
  methods: {
    async submitFaceScansForm() {
      this.loading = true;
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        if (!this.patient_id) {
          throw new Error(
            "Patient ID is not available. Make sure a patient is selected."
          );
        }

        const url = `${fasdBackendURL}/api/facescans/${this.patient_id}/facescans`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            access_token: token,
            token_type: "bearer",
          },
          body: JSON.stringify({
            new_facescan: {
              scan_date: this.faceScansForm.scanDate,
              imaging_modality: this.faceScansForm.imagingModality,
              mesh: "",
              texture: "",
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const fetchedData = await response.json();
        this.facescan_id =
          fetchedData.facescan_id || Object.values(fetchedData)[5];
        console.log("Facescan inserted with ID:", this.facescan_id);

        if (this.faceScansForm.imagingModality === "Canfield Stereoscopic") {
          const meshData = new FormData();
          meshData.append("meshfile", this.faceScansForm.faceMeshFile);

          const meshResponse = await fetch(
            `${fasdBackendURL}/api/facescans/facescanmesh/${this.facescan_id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                access_token: token,
                token_type: "bearer",
              },
              body: meshData,
            }
          );

          if (!meshResponse.ok) {
            throw new Error(`HTTP error! status: ${meshResponse.status}`);
          }

          const meshResult = await meshResponse.json();
          console.log("Facescan mesh inserted:", meshResult);

          this.meshFileURL = URL.createObjectURL(
            this.faceScansForm.faceMeshFile
          );
        }

        if (this.faceScansForm.imagingModality === "Canfield Stereoscopic") {
          const textureData = new FormData();
          textureData.append("texturefile", this.faceScansForm.textureFile);

          const textureResponse = await fetch(
            `${fasdBackendURL}/api/facescans/facescantexture/${this.facescan_id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                access_token: token,
                token_type: "bearer",
              },
              body: textureData,
            }
          );

          if (!textureResponse.ok) {
            throw new Error(`HTTP error! status: ${textureResponse.status}`);
          }

          const textureResult = await textureResponse.json();
          console.log("Facescan texture inserted:", textureResult);

          this.textureFileURL = URL.createObjectURL(
            this.faceScansForm.textureFile
          );
        }
        if (this.faceScansForm.imagingModality === "FitsYou") {
          if (!this.faceScansForm.zipFile) {
            throw new Error(
              "ZIP file is required for FitsYou imaging modality."
            );
          }

          const zipData = new FormData();
          zipData.append(
            "fitsyouzipfile",
            this.faceScansForm.zipFile,
            this.faceScansForm.zipFile.name
          );

          const zipResponse = await fetch(
            `${fasdBackendURL}/api/facescans/facescanfitsyouzipfile/${this.facescan_id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                access_token: token,
                token_type: "bearer",
              },
              body: zipData,
            }
          );

          if (!zipResponse.ok) {
            const errorText = await zipResponse.text();
            throw new Error(
              `HTTP error! status: ${zipResponse.status}, response: ${errorText}`
            );
          }
          console.log(
            "FitsYou ZIP file uploaded successfully:",
            await zipResponse.json()
          );
        }

        if (this.faceScansForm.imagingModality === "Canfield Stereoscopic (TOM)") {
          if (!this.faceScansForm.tomFile) {
            throw new Error("TOM file is required for the TOM imaging modality.");
          }

          const tomData = new FormData();
          tomData.append("tomfile", this.faceScansForm.tomFile, this.faceScansForm.tomFile.name);

          const tomResponse = await fetch(
            `${fasdBackendURL}/api/facescans/facescantom/${this.facescan_id}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                access_token: token,
                token_type: "bearer",
              },
              body: tomData,
            }
          );

          if (!tomResponse.ok) {
            const t = await tomResponse.text();
            throw new Error(`HTTP error! status: ${tomResponse.status}, response: ${t}`);
          }

          console.log("TOM file converted and saved successfully:", await tomResponse.json());
        }

        this.$emit("upload-success", fetchedData);
        this.submittedFaceScans = true;
        this.submitted = true;
        this.showFaceScanSuccessMessage = true;
        this.showFiles = true;

        setTimeout(() => {
          this.showFaceScanSuccessMessage = false;
          this.displayGenerateLandMark = true;
        }, 3000);
      } catch (error) {
        console.error("There was an error adding the face scan:", error);
      } finally {
        this.loading = false;
      }
    },
    dropFile(event, field) {
      const file = event.dataTransfer
        ? event.dataTransfer.files[0]
        : event.target.files[0];
      this.faceScansForm[field] = file;

      // Update display names
      if (field === "faceMeshFile") {
        this.pendingFaceMeshFile = file;
        this.showVTKModal = true;
      }
      if (field === "textureFile") this.textureFile_name = file.name;
      if (field === "zipFile") this.zipFile_name = file.name;
      if (field === "tomFile") this.tomFile_name = file.name;

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.$refs[field].files = dataTransfer.files;
    },
     vtkConfirmed(blob) {
      const originalName = this.pendingFaceMeshFile.name;
      const modifiedFile = new File([blob], originalName, { type: 'text/plain' });

      this.modifiedFaceMeshBlob = blob;
      this.faceMeshFile_name = originalName + " (modified)";
      this.faceScansForm.faceMeshFile = modifiedFile;
      this.showVTKModal = false;
    },

    vtkClosed() {
      this.showVTKModal = false;
    },

    getFile(field) {
      this.$refs[field].click();
    },
  },
};
</script>

<style scoped></style>
